import type { ConfirmationContentProps } from "./ClientTools";
import styles from "./CreateAgentConfirmation.module.css";

interface AgentGoalParams {
  goal_type?: unknown;
  label_vocabulary?: Record<string, unknown>;
  summary_format_spec_prompt?: unknown;
  event_vocabulary?: Record<string, unknown>;
  tag_vocabulary?: Record<string, unknown>;
  collection_enabled?: unknown;
  event_focus_prompt?: unknown;
}

interface AgentParams {
  name?: unknown;
  description?: unknown;
  model_profile?: unknown;
  kickoff_message?: unknown;
  goals?: unknown;
  variables?: unknown;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

function previewKeys(keys: string[]): string {
  return keys.length <= 3
    ? keys.join(", ")
    : `${keys.slice(0, 3).join(", ")}, +${keys.length - 3} more`;
}

function summarizeGoal(goal: AgentGoalParams): string {
  if (goal.goal_type === "dataset_summary") {
    return "Dataset summary";
  }
  if (goal.goal_type === "dataset_triage") {
    const vocab = goal.label_vocabulary;
    if (vocab && typeof vocab === "object" && !Array.isArray(vocab)) {
      const labels = Object.keys(vocab).filter((k) => k.trim() !== "");
      if (labels.length === 0) {
        return "Dataset triage (no labels yet)";
      }
      return `Dataset triage: ${previewKeys(labels)}`;
    }
    return "Dataset triage";
  }
  if (goal.goal_type === "create_events") {
    const vocab = goal.event_vocabulary;
    const collectionSuffix =
      goal.collection_enabled === true ? " → collection" : "";
    if (vocab && typeof vocab === "object" && !Array.isArray(vocab)) {
      const names = Object.keys(vocab).filter((k) => k.trim() !== "");
      if (names.length === 0) {
        return `Create events (no event types yet)${collectionSuffix}`;
      }
      return `Create events: ${previewKeys(names)}${collectionSuffix}`;
    }
    return `Create events${collectionSuffix}`;
  }
  return "Unknown goal";
}

/**
 * Read-only preview of the draft the model wants to seed into the
 * Agent editor. Surfaces the proposed name, a short description if
 * provided, the count and shape of declared goals, and whether a
 * kickoff message is included. The editor itself is the rich
 * confirmation surface — this card just gives the user enough signal
 * to decide whether to approve the navigation when Auto is off.
 */
export function CreateAgentConfirmation({ params }: ConfirmationContentProps) {
  const p = params as AgentParams;
  const name = asString(p.name) ?? "(unnamed)";
  const description = asString(p.description);
  const modelProfile = asString(p.model_profile);
  const kickoff = asString(p.kickoff_message);
  const goals = Array.isArray(p.goals) ? (p.goals as AgentGoalParams[]) : [];
  const variableCount = Array.isArray(p.variables) ? p.variables.length : 0;

  return (
    <div className={styles.container}>
      <div className={styles.lead}>
        Open the Agent editor pre-filled with this draft:
      </div>
      <div className={styles.card}>
        <div className={styles.name}>{name}</div>
        {description && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Description</span>
            <span className={styles.fieldValue}>{description}</span>
          </div>
        )}
        {modelProfile && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Model</span>
            <span className={styles.fieldValue}>{modelProfile}</span>
          </div>
        )}
        {kickoff && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Kickoff</span>
            <span className={styles.fieldValue}>{kickoff}</span>
          </div>
        )}
        {goals.length > 0 && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              Goal{goals.length === 1 ? "" : "s"}
            </span>
            <ul className={styles.goalsList}>
              {goals.map((goal, idx) => (
                <li key={idx}>{summarizeGoal(goal)}</li>
              ))}
            </ul>
          </div>
        )}
        {variableCount > 0 && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Variables</span>
            <span className={styles.fieldValue}>{variableCount}</span>
          </div>
        )}
      </div>
      <div className={styles.footnote}>
        Approving navigates you to the editor — the agent isn&rsquo;t saved
        until you click Create there.
      </div>
    </div>
  );
}
